import Lottie from 'lottie-react';
import { Fragment } from 'react';

import { ActionStep, ActionStepTemplate, RemoteCopy } from 'types';

import ActionStepCard from './ActionStepCard';
import ActionStepForm from './ActionStepForm';

import Sparkle from 'assets/lottie/sparkle-effect.json';

import Fade from '@mui/material/Fade';

type ActionStepSummaryProps = {
    actionSuggestions: null | RemoteCopy;
    actionType: ActionStep[];
    bonusType?: 'base' | 'extra';
    editAction: number[];
    failedIndices?: Map<number, string>;
    from: string;
    handleEditClick: (index: number) => void;
    intakeGoals: ActionStep[];
    isExtraBonusParticipant?: boolean;
    onSparkleLoop?: () => void;
    remainingBaseBonusAmount?: number;
    remainingBonusAmount: number;
    remainingScfBonusAmount?: number;
    setDisplayNeedBtns?: (displayNeedBtns: boolean) => void;
    setEditAction: (editAction: number[]) => void;
    setGuide: (guide: boolean) => void;
    setIntakeGoals: (intakeGoals: ActionStep[]) => void;
    setShowForm: (showForm: boolean) => void;
    showForm: boolean;
    sparkleIndex?: number | null;
    temporaryAction: ActionStep | ActionStepTemplate | null;
};

const isExtraPoolAction = (action: any) =>
    action?.classification === 'capital_foundation_pool' ||
    action?._serverData?.classification === 'capital_foundation_pool';

const ActionStepSummary: React.FC<ActionStepSummaryProps> = ({
    actionSuggestions,
    actionType,
    editAction,
    failedIndices,
    from,
    handleEditClick,
    intakeGoals,
    isExtraBonusParticipant = false,
    onSparkleLoop,
    remainingBaseBonusAmount,
    remainingBonusAmount,
    remainingScfBonusAmount,
    setDisplayNeedBtns,
    setEditAction,
    setGuide,
    setIntakeGoals,
    setShowForm,
    showForm,
    sparkleIndex,
    temporaryAction
}) => {
    const count = actionType ? actionType.length : 0;

    const customStep = false;

    const editForm = (index: number) => {
        if (temporaryAction && index === intakeGoals.length) {
            return {
                actionIndex: index,
                costInputState:
                    temporaryAction.bonus || temporaryAction.amount || 0,
                dateInputState: temporaryAction.due_at,
                descriptionInput: true,
                descriptionInputState: temporaryAction.description
            };
        }

        return {
            actionIndex: index,
            costInputState: intakeGoals[index].bonus
                ? intakeGoals[index].bonus!
                : intakeGoals[index].amount,
            customGoalInputState: intakeGoals[index].customGoals,
            dateInputState: intakeGoals[index].due_at,
            descriptionInput: true,
            descriptionInputState: intakeGoals[index].description,
            goalInputState: intakeGoals[index].goals,
            qolMeasureState: intakeGoals[index].qolMeasure
        };
    };

    const getEditPoolProps = (index: number) => {
        const action =
            temporaryAction && index === intakeGoals.length
                ? temporaryAction
                : intakeGoals[index];
        const isExtra = isExtraPoolAction(action);
        const editBonusType: 'base' | 'extra' = isExtra ? 'extra' : 'base';
        const editRemaining = isExtra
            ? remainingScfBonusAmount ?? remainingBonusAmount
            : remainingBaseBonusAmount ?? remainingBonusAmount;

        return {
            bonusType: editBonusType,
            remainingBonusAmount: editRemaining
        };
    };

    return (
        <>
            {actionType.map((action, index) => {
                if (editAction.includes(index)) {
                    const editPoolProps = getEditPoolProps(index);

                    return (
                        <Fade
                            in={!showForm}
                            timeout={500}
                            key={`edit-fade-${index}`}
                        >
                            <div style={{ width: '100%' }}>
                                <ActionStepForm
                                    actionSuggestions={actionSuggestions}
                                    bonusType={editPoolProps.bonusType}
                                    editAction={editAction}
                                    editForm={editForm(index)}
                                    errorMessage={failedIndices?.get(index)}
                                    from={from}
                                    intakeGoals={intakeGoals}
                                    isExtraBonusParticipant={
                                        isExtraBonusParticipant
                                    }
                                    onClick={() => setDisplayNeedBtns!(true)}
                                    remainingBonusAmount={
                                        editPoolProps.remainingBonusAmount
                                    }
                                    setEditAction={setEditAction}
                                    setGuide={setGuide}
                                    setIntakeGoals={setIntakeGoals}
                                    setShowForm={setShowForm}
                                    showEdit={true}
                                />
                            </div>
                        </Fade>
                    );
                }

                const cardContent = (
                    <div style={{ position: 'relative', width: '100%' }}>
                        {index === sparkleIndex && (
                            <Lottie
                                animationData={Sparkle}
                                loop={3}
                                onLoopComplete={onSparkleLoop}
                                style={{
                                    position: 'absolute',
                                    zIndex: 1,
                                    height: '56px',
                                    right: '-8px',
                                    top: '16px',
                                    width: '56px'
                                }}
                            />
                        )}
                        <ActionStepCard
                            action={action}
                            actionType={actionType}
                            count={count}
                            customStep={customStep}
                            handleEditClick={handleEditClick}
                            index={index}
                            intakeGoals={intakeGoals}
                        />
                    </div>
                );

                return <Fragment key={`fade-${index}`}>{cardContent}</Fragment>;
            })}
        </>
    );
};

export default ActionStepSummary;
